import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
	AlarisComplexTableComponent,
	AlarisDialogService, AlarisFilterManagerComponent,
	AlarisLocalStorageService,
	AlarisTableStateService,
	EditPanelWidth,
	RowActionSimple,
	TABLE_STATE_SERVICE_INJECTOR, TableEntityField, TableFiltersIndicator
} from '@campaign-portal/components-library';
import { Client, Product } from '@helpers/types/models';
import { STORAGE } from '@helpers/types/consts';
import { FilterType, Paging } from '@campaign-portal/namespace/common/rpc.params';
import { BehaviorSubject } from 'rxjs';
import { TableSettingsService } from '@helpers/api/table-settings.service';
import { TableTypes } from '@campaign-portal/namespace/entities/table-settings/specs';
import { ClientsService } from './clients.service';
import { ClientsFieldsService } from './clients.fields.service';
import { ProductsService } from '@helpers/repo/products.service';
import { ClientsDeleteDialogComponent } from './dialog/dialog.component';
import { EditClientComponent } from './edit-client/edit-client.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RepoService, RepoType } from '@helpers/repo/repo.service';
import { UserService } from '../../auth/user.service';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { EntityField, InputComplexType, InputType } from '@campaign-portal/namespace/common/entityField';
import { eapiEnable } from '../../app.module';
enum FilterVariables {
	LOGIN = 'login',
	AVAILABLE_INTERFACES = 'availableInterfaces',
	PRODUCT_ID = 'productId',
}

const login = new TableEntityField({
	id: null,
	name: 'clients.login',
	variable: FilterVariables.LOGIN,
	filterType: FilterType.LIKE,
	required: false,
	type: InputType.TEXT
});
const availableInterfaces =  new TableEntityField({
	id: null,
	name: 'clients.available_interfaces',
	variable: FilterVariables.AVAILABLE_INTERFACES,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: ['http', 'enum', 'smsrt']
});
const productId =  new TableEntityField({
	id: null,
	name: 'clients.client_product',
	variable: FilterVariables.PRODUCT_ID,
	filterType: FilterType.LIKE,
	required: false,
	type: InputComplexType.MULTISELECT,
	data: []
});
@Component({
	selector: 'app-clients',
	templateUrl: './clients.component.html',
	styleUrls: [
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
		'./clients.component.scss'
	],
	providers: [
		AlarisTableStateService,
		{ provide: TABLE_STATE_SERVICE_INJECTOR, useExisting: AlarisTableStateService }
	],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClientsComponent extends AlarisComplexTableComponent<Client> implements OnDestroy, AfterViewInit {
	private headers: EntityField[] = [];
	private productsMap = new Map<any, any>;
	private filters: TableFiltersIndicator = new Map()
		.set(FilterVariables.LOGIN, { enabled: true, filterType: FilterType.LIKE })
		.set(FilterVariables.AVAILABLE_INTERFACES, { enabled: true, filterType: FilterType.LIKE })
		.set(FilterVariables.PRODUCT_ID, { enabled: true, value: [], filterType: FilterType.LIKE })
	override readonly rowActions: RowActionSimple<any>[] = [
		{
			icon: 'icon-edit',
			label: 'actions.edit',
			action: this.edit.bind(this),
			disabled: (): boolean => {
				return !this.user.allowed(['clients.edit'])
			}
		},
		{
			icon: 'icon-delete',
			label: 'actions.delete',
			action: this.delete.bind(this),
			highlight: true,
			separator: true,
			disabled: (): boolean => {
				return !this.user.allowed(['clients.edit'])
			}
		}
	];
	override readonly dblClickRowAction = this.edit;
	override pageSize = parseInt(this.storage.get(STORAGE.PAGE_SIZE) ?? '25', 10);
	override readonly pageSizeOptions = [25, 50, 100, 200];
	override readonly paging: Paging = {
		Skip: this.activePage,
		Take: this.pageSize
	};

	readonly exporting$ = new BehaviorSubject<boolean>(false);

	constructor(
		dataService: ClientsService,
		public override readonly fieldsService: ClientsFieldsService,
		tableSettingsService: TableSettingsService,
		private readonly repo: RepoService,
		private readonly storage: AlarisLocalStorageService,
		private readonly dialog: AlarisDialogService,
		private readonly clientsService: ClientsService,
		protected readonly user: UserService,
		public readonly productsService: ProductsService
	) {
		super(
			dataService,
			fieldsService,
			tableSettingsService,
			TableTypes.INTERNAL
		);
		this.headers = [login, availableInterfaces, productId];
		this.state.filters
			.set('maxSessions', { enabled: false });
		this.repo.load(['hlr', 'sms']).pipe(takeUntilDestroyed()).subscribe();
	}

	override ngOnDestroy(): void {
		super.ngOnDestroy();
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	edit(client?: Client): void {
		this.editPanel.open<EditClientComponent, {
			client: Client
		}, boolean>(EditClientComponent, EditPanelWidth.SM, { client })
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	delete(client: Client): void {
		this.dialog.open<boolean>(ClientsDeleteDialogComponent, { autoFocus: false, data: { client } })
			.closed
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if ( result ) {
					this.load();
				}
			});
	}

	export(): void {
		this.exporting$.next(true);
		this.clientsService.export(this.total, this.state.readParams)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.exporting$.next(false);
			});
	}

	override ngAfterViewInit(): void {
		super.ngAfterViewInit();
		if (!eapiEnable) {
			productId.type = InputType.TEXT;
		}
		this.productsService.map$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(list => {
				this.productsMap = list;
			});
		this.productsService.clientProducts$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((list) => {
				if (eapiEnable) {
					const data = list.map((item) => {
						return { name: item.product_caption, value: item.product_id };
					});
					productId.data = data;
					this.setRefBooksInFields({
						productId: { list$: new BehaviorSubject<{ name: string; value: number }[]>(data) }
					});
					productId.type = InputComplexType.MULTISELECT;
					this.filters
						.set(FilterVariables.PRODUCT_ID, { enabled: true, value: [], filterType: FilterType.LIKE });
				} else {
					productId.type = InputType.TEXT;
					this.filters
						.set(FilterVariables.PRODUCT_ID, { enabled: true, value: null, filterType: FilterType.LIKE });
				}
			});
	}

	transform(id: Id<exist> | string): Product | null {
		if ( typeof id === 'string' ) {
			id = parseInt(id, 10);
		}
		return this.transformProduct(id) as (Product | null);
	}

	override editTableFilter(): void {
		this.editPanel.open(AlarisFilterManagerComponent, EditPanelWidth.MD, {
			filtersChange: this.filtersChange,
			filters: this.filters,
			tHeaders: this.headers
		});
	}

	private transformProduct(id: Id<exist>): Product | null {
		return this.productsService.find<Product>(id, this.productsMap, RepoType.PRODUCT);
	}

	protected readonly eapiEnable = eapiEnable;
}
